import type { CSSProperties, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { useViewAsKid } from './viewAsKid/ViewAsKidContext'
import { useChildren } from './api/queries'
import { kidAccent } from './lib/kidAccent'

export function Layout({ children }: { children: ReactNode }) {
  const { logout } = useAuth()
  const { viewingChildId, viewAsKid, viewAsParent } = useViewAsKid()
  const { data: allChildren } = useChildren()
  const navigate = useNavigate()

  const viewingChild = allChildren?.find((c) => c.id === viewingChildId)

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  // Per the plan: this is a presentation filter, not a second access boundary. Switching
  // back to parent view needs no re-auth because the parent session never stopped being
  // valid - it only ever controlled what this nav chooses to render.
  if (viewingChildId !== null) {
    const style = { '--kid-accent': kidAccent(viewingChildId) } as CSSProperties
    return (
      <div className="app">
        <header className="topbar kid-mode" style={style}>
          <span className="brand">ParentBank</span>
          <span className="viewing-as">
            <span className="kid-dot" />
            Viewing as <strong>{viewingChild?.displayName ?? '…'}</strong>
          </span>
          <button type="button" onClick={viewAsParent} className="link-button">
            Back to parent view
          </button>
        </header>
        <main>{children}</main>
      </div>
    )
  }

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">ParentBank</span>
        <nav>
          <Link to="/">Dashboard</Link>
          <Link to="/rates">Rates</Link>
          <Link to="/allowance">Allowance</Link>
        </nav>
        <div className="topbar-right">
          {allChildren && allChildren.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => {
                const id = Number(e.target.value)
                if (id) {
                  viewAsKid(id)
                  navigate(`/children/${id}`)
                }
              }}
            >
              <option value="" disabled>
                View as kid…
              </option>
              {allChildren.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          )}
          <button type="button" onClick={handleLogout} className="link-button">
            Log out
          </button>
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}
